import { GsFormFieldNumber, GsFormSchema } from '../../../components';
import { GsStructCompanyInfoBoxContent, GsStructCompanyInfoElementPosition, GsStructVerticalAlignment } from '../../../models';
import { BASIC_SCHEMA_FIELDS } from './../basics/basic.schema';
import { SPACING_SCHEMA } from './spacing.schema';

/**
 * The schema for the company info component form.
 * Defines the fields to display for the form.
 */
export const COMPANY_INFO_COMPONENT_FORM_SCHEMA: GsFormSchema = {
  fields: [
    ...BASIC_SCHEMA_FIELDS,
    ...SPACING_SCHEMA,
    // Box
    {
      formControlName: 'showBox',
      label: 'components.companyInfo.schema.boxOverlay',
      type: 'checkbox',
      default: false
    },
    {
      formControlName: 'boxContentMode',
      label: 'components.companyInfo.schema.contentInsideBox',
      type: 'select',
      default: GsStructCompanyInfoBoxContent.contact,
      valueType: 'number',
      options: [
        { text: 'components.companyInfo.schema.contentInsideBoxNone', value: GsStructCompanyInfoBoxContent.none },
        { text: 'components.companyInfo.schema.contentInsideBoxContact', value: GsStructCompanyInfoBoxContent.contact },
        { text: 'components.companyInfo.schema.contentInsideBoxText', value: GsStructCompanyInfoBoxContent.text }
      ],
      if: {
        args: [
          {
            formControlName: 'showBox',
            value: true
          }
        ]
      }
    },
    {
      formControlName: 'boxAlignment',
      label: 'components.companyInfo.schema.boxAlignment',
      type: 'select',
      default: GsStructCompanyInfoElementPosition.right,
      valueType: 'number',
      options: [
        { text: 'components.companyInfo.schema.boxAlignmentLeft', value: GsStructCompanyInfoElementPosition.left },
        { text: 'components.companyInfo.schema.boxAlignmentRight', value: GsStructCompanyInfoElementPosition.right }
      ],
      groupingName: 'components.companyInfo.schema.box',
      if: {
        args: [
          {
            formControlName: 'showBox',
            value: true
          }
        ]
      }
    },
    {
      formControlName: 'boxInnerVerticalAlignment',
      label: 'components.companyInfo.schema.boxInnerVerticalAlignment',
      type: 'select',
      default: GsStructVerticalAlignment.middle,
      valueType: 'number',
      options: [
        { text: 'components.companyInfo.schema.boxInnerVerticalAlignmentTop', value: GsStructVerticalAlignment.top },
        { text: 'components.companyInfo.schema.boxInnerVerticalAlignmentMiddle', value: GsStructVerticalAlignment.middle },
        { text: 'components.companyInfo.schema.boxInnerVerticalAlignmentBottom', value: GsStructVerticalAlignment.bottom }
      ],
      groupingName: 'components.companyInfo.schema.box',
      if: {
        args: [
          {
            formControlName: 'showBox',
            value: true
          }
        ]
      }
    },
    {
      formControlName: 'boxWidth',
      label: 'components.companyInfo.schema.boxWidth',
      type: 'select',
      default: '4',
      valueType: 'string',
      options: [
        { value: '3', text: '25%' },
        { value: '4', text: '30%' },
        { value: '5', text: '40%' },
        { value: '6', text: '50%' },
        { value: '7', text: '60%' },
        { value: '9', text: '75%' }
      ],
      groupingName: 'components.companyInfo.schema.box',
      if: {
        args: [
          {
            formControlName: 'showBox',
            value: true
          }
        ]
      }
    },
    {
      formControlName: 'boxPadding',
      label: 'components.companyInfo.schema.paddingInsideBox',
      type: 'padding',
      useTailwindClasses: true,
      default: { top: 4, right: 4, bottom: 4, left: 4 },
      groupingName: 'components.companyInfo.schema.box',
      if: {
        args: [
          {
            formControlName: 'showBox',
            value: true
          }
        ]
      }
    },
    {
      formControlName: 'boxBackgroundColor',
      label: 'components.companyInfo.schema.boxBackgroundColor',
      type: 'color',
      default: 'light-main',
      groupingName: 'components.companyInfo.schema.box',
      if: {
        args: [
          {
            formControlName: 'showBox',
            value: true
          }
        ]
      }
    },
    //Text on top inside box
    {
      formControlName: 'boxTitle',
      label: 'components.companyInfo.schema.titleInsideBox',
      type: 'text',
      default: 'Kontaktiere uns',
      groupingName: 'components.companyInfo.schema.boxContent',
      if: {
        args: [
          {
            formControlName: 'showBox',
            value: true
          }
        ]
      }
    },
    {
      formControlName: 'showAddress',
      label: 'components.companyInfo.schema.showAddress',
      type: 'checkbox',
      default: true,
      groupingName: 'components.companyInfo.schema.boxContent',
      if: {
        chaining: 'and',
        args: [
          {
            formControlName: 'showBox',
            value: true
          },
          {
            formControlName: 'boxContentMode',
            value: GsStructCompanyInfoBoxContent.contact
          }
        ]
      }
    },
    {
      formControlName: 'showContact',
      label: 'components.companyInfo.schema.showContact',
      type: 'checkbox',
      default: true,
      groupingName: 'components.companyInfo.schema.boxContent',
      if: {
        chaining: 'and',
        args: [
          {
            formControlName: 'showBox',
            value: true
          },
          {
            formControlName: 'boxContentMode',
            value: GsStructCompanyInfoBoxContent.contact
          }
        ]
      }
    },
    {
      formControlName: 'showPhone',
      label: 'components.companyInfo.schema.showPhoneNumber',
      type: 'checkbox',
      default: true,
      groupingName: 'components.companyInfo.schema.boxContent',
      if: {
        chaining: 'and',
        args: [
          {
            formControlName: 'showContact',
            value: true
          },
          {
            formControlName: 'boxContentMode',
            value: GsStructCompanyInfoBoxContent.contact
          }
        ]
      }
    },
    {
      formControlName: 'showFax',
      label: 'components.companyInfo.schema.showFaxNumber',
      type: 'checkbox',
      default: true,
      groupingName: 'components.companyInfo.schema.boxContent',
      if: {
        chaining: 'and',
        args: [
          {
            formControlName: 'showContact',
            value: true
          },
          {
            formControlName: 'boxContentMode',
            value: GsStructCompanyInfoBoxContent.contact
          }
        ]
      }
    },
    {
      formControlName: 'showEmail',
      label: 'components.companyInfo.schema.showEmailAddress',
      type: 'checkbox',
      default: true,
      groupingName: 'components.companyInfo.schema.boxContent',
      if: {
        chaining: 'and',
        args: [
          {
            formControlName: 'showContact',
            value: true
          },
          {
            formControlName: 'boxContentMode',
            value: GsStructCompanyInfoBoxContent.contact
          }
        ]
      }
    },
    //Text
    {
      formControlName: 'boxText',
      label: 'components.companyInfo.schema.textBox',
      type: 'text',
      default: 'Beispieltext',
      groupingName: 'components.companyInfo.schema.boxContent',
      if: {
        chaining: 'and',
        args: [
          {
            formControlName: 'showBox',
            value: true
          },
          {
            formControlName: 'boxContentMode',
            value: GsStructCompanyInfoBoxContent.text
          }
        ]
      }
    },
    //Opening Hours
    {
      formControlName: 'showOpeningHours',
      label: 'components.companyInfo.schema.showOpeningHours',
      type: 'checkbox',
      default: true,
      groupingName: 'components.companyInfo.schema.openingHours',
      if: {
        args: [
          {
            formControlName: 'showBox',
            value: true
          }
        ]
      }
    },
    {
      formControlName: 'showClosingHours',
      label: 'components.companyInfo.schema.showClosingHours',
      type: 'checkbox',
      default: false,
      groupingName: 'components.companyInfo.schema.openingHours',
      if: {
        args: [
          {
            formControlName: 'showOpeningHours',
            value: true
          }
        ]
      }
    },
    //Link
    {
      formControlName: 'showLink',
      label: 'components.companyInfo.schema.addLinkAtBottom',
      type: 'checkbox',
      default: false,
      groupingName: 'components.companyInfo.schema.link',
      if: {
        args: [
          {
            formControlName: 'showBox',
            value: true
          }
        ]
      }
    },
    {
      formControlName: 'linkText',
      label: 'components.companyInfo.schema.linkText',
      type: 'text',
      default: '',
      placeholder: 'components.companyInfo.schema.linkTextPlaceholder',
      groupingName: 'components.companyInfo.schema.link',
      if: {
        args: [
          {
            formControlName: 'showLink',
            value: true
          }
        ]
      }
    },
    //Map
    {
      formControlName: 'mapHeight',
      label: 'components.companyInfo.schema.mapHeight',
      type: 'number',
      isResponsive: true,
      default: { default: 500 },
      groupingName: 'components.companyInfo.schema.map'
    } as GsFormFieldNumber,
    {
      formControlName: 'mapZoom',
      label: 'components.companyInfo.schema.mapZoom',
      type: 'number',
      default: 10,
      groupingName: 'components.companyInfo.schema.map'
    } as GsFormFieldNumber
  ]
};
